import { useIsMobile } from "@hooks/useIsMobile"
import { useRoute, useRouterEvents, useRouterState } from "eddev/routing"
import { useMemo } from "react"

/**
 * On mobile, display film routes in a drawer when they have been tapped.
 *
 * When refreshed, the route will be displayed as a regular page.
 *
 * In the future, other routes could be added too.
 */
export function useRoutesDisplay() {
  const route = useRoute()
  const isMobile = useIsMobile()

  const history = useRouterState((state) => state.history)

  const display = useMemo(() => {
    const backgroundRoute = isMobile ? history.filter((route) => !route.linkData?.modal).at(-1) : null
    const filmRoutes =
      backgroundRoute && isMobile
        ? history.slice(history.indexOf(backgroundRoute)).filter((route) => route.linkData?.modal)
        : []
    return {
      mainRoute: backgroundRoute ?? route,
      drawerRoutes: filmRoutes,
    }
  }, [isMobile, history])

  // Only restore scroll position if there are no drawer routes
  useScrollRestoration(display.drawerRoutes.length === 0)

  return display
}

function useScrollRestoration(enabled: boolean) {
  const restore = (scroll: { left: number; top: number }) =>
    document.scrollingElement?.scrollTo({
      behavior: "instant",
      left: scroll.left,
      top: scroll.top,
    })

  useRouterEvents((event) => {
    if (!enabled) return
    if (event.type === "navigate:changed") {
      // If the pathname and search term are the same, then we're on the same page
      const isSameRoute =
        event.currentRoute.pathname === event.lastRoute.pathname && event.currentRoute.search === event.lastRoute.search
      if (isSameRoute) return
      if (event.currentRoute.returnState) {
        restore({
          top: event.currentRoute.returnState.scrollTop ?? 0,
          left: event.currentRoute.returnState.scrollLeft ?? 0,
          // direction: "back",
        })
      } else {
        restore({
          top: 0,
          left: 0,
          // direction: "forward",
        })
      }
    }
  })
}
